import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { faqService } from '../../services/api';
import toast from 'react-hot-toast';

const FAQDetail = () => {
  const { id } = useParams();
  const [faq, setFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const response = await faqService.getById(id);
        setFaq(response.data.data);
      } catch (error) {
        console.error('Failed to fetch FAQ:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaq();
  }, [id]);

  const handleFeedback = async (isHelpful) => {
    try {
      await faqService.recordFeedback(id, isHelpful);
      setFeedbackGiven(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8"></div>
      </div>
    );
  }

  if (!faq) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">FAQ not found</h2>
        <Link to="/faq" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
          Back to Knowledge Base
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/faq" className="hover:text-gray-700">Knowledge Base</Link>
        <span>/</span>
        <span>{faq.category_name}</span>
      </div>

      {/* Question */}
      <div className="card">
        <div className="card-body">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{faq.question}</h1>
          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
            <span className="badge badge-blue">{faq.category_name}</span>
            <span>{faq.view_count} views</span>
          </div>

          {/* Answer */}
          <div className="prose max-w-none">
            <div className="whitespace-pre-wrap text-gray-700">{faq.answer}</div>
          </div>

          {/* Keywords */}
          {faq.keywords && faq.keywords.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-500 mb-2">Related keywords:</p>
              <div className="flex flex-wrap gap-2">
                {faq.keywords.map((keyword, index) => (
                  <span key={index} className="badge badge-gray">{keyword}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Feedback */}
      <div className="card">
        <div className="card-body text-center">
          {!feedbackGiven ? (
            <>
              <p className="text-gray-700 mb-4">Was this article helpful?</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => handleFeedback(true)}
                  className="btn-secondary flex items-center"
                >
                  <svg className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                  Yes, helpful
                </button>
                <button
                  onClick={() => handleFeedback(false)}
                  className="btn-secondary flex items-center"
                >
                  <svg className="h-5 w-5 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                  </svg>
                  No, not helpful
                </button>
              </div>
            </>
          ) : (
            <p className="text-green-600">Thank you for your feedback!</p>
          )}
        </div>
      </div>

      {/* Related Actions */}
      <div className="flex justify-between items-center">
        <Link to="/faq" className="text-primary-600 hover:text-primary-700">
          &larr; Back to Knowledge Base
        </Link>
        <Link to="/tickets/new" className="btn-primary btn-sm">
          Still need help? Create a ticket
        </Link>
      </div>
    </div>
  );
};

export default FAQDetail;
